import * as React from "react";
import { format } from "date-fns";
import { DayPicker } from "react-day-picker";
import { NavArrowRight, NavArrowLeft } from "iconoir-react";
import { Input, Popover, Typography } from "@material-tailwind/react";
import { useFormContext } from 'react-hook-form'
import { findInputError, isFormInvalid } from '../utils'

interface DatePickerInputProps {
    name: string;
    label?: string;
    placeholder?: string;
    validation?: object;
    wrapperClass?: string;
    labelClass?: string;
    labelColor?: string;
    inputClass?: string;
}

export function FormDatepickerInput({
    name,
    label,
    placeholder = "Select a date",
    validation = {},
    wrapperClass = "w-full",
    labelClass,
    labelColor,
    inputClass,
}: DatePickerInputProps) {
    const [isOpen, setIsOpen] = React.useState(false);

    const {
        register,
        setValue,
        watch,
        formState: { errors },
    } = useFormContext();

    const inputErrors = findInputError(errors, name);
    const isInvalid = isFormInvalid(inputErrors);

    const value = watch(name) as Date | null;

    React.useEffect(() => {
        register(name, validation);
    }, [register, name, validation]);

    const handleDateSelect = (date: Date | undefined) => {
        setValue(name, date, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
        setIsOpen(false);
    };

    return (
        <div className={wrapperClass}>
            <div className="flex justify-between">
                {label && (
                    <Typography
                        as="label"
                        type="small"
                        color="default"
                        className={`${labelClass ? labelColor : "font-semibold"} ${
                            isInvalid ? "text-red-500" : labelColor ? labelColor : "text-black"
                        }`}
                    >
                        {label}
                    </Typography>
                )}
                {isInvalid && (
                    <Typography
                        variant="small"
                        color="error"
                        className="flex items-center gap-1 font-normal"
                    >
                        {inputErrors.error.message}
                    </Typography>
                )}
            </div>
            <Popover placement="bottom" open={isOpen} onOpenChange={setIsOpen}>
                <Popover.Trigger className="w-full">
                    <div className="w-full" onClick={() => setIsOpen(true)}>
                        <Input
                            type="text"
                            size="lg"
                            readOnly
                            placeholder={placeholder}
                            value={value ? format(value, "yyyy-MM-dd") : ""}
                            className={`${inputClass} ${
                                isInvalid
                                    ? "border-red-300 focus:ring-0 focus:outline-none focus:border-red-500"
                                    : ""
                            }`}
                        />
                    </div>
                </Popover.Trigger>
                <Popover.Content className="z-[9999]">
                    <Popover.Arrow />
                    <DayPicker
                        mode="single"
                        selected={value || undefined}
                        onSelect={handleDateSelect}
                        showOutsideDays
                        className="border-0"
                        classNames={{
                            day_hidden: "invisible",
                            nav: "fixed flex items-center justify-between left-0 top-2 w-full",
                            day: "h-9 w-9 p-0 text-center ",
                            day_range_end: "day-range-end",
                            table: "w-full border-collapse",
                            nav_button_next: "absolute right-1.5",
                            nav_button_previous: "absolute left-1.5",
                            head_row: "flex font-medium text-black dark:text-white",
                            day_disabled: "text-foreground opacity-50",
                            head_cell: "m-0.5 w-9  text-sm",
                            day_today: "rounded-md bg-primary text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white",
                            caption_label: "text-sm font-medium block text-center text-black dark:text-white mb-2",
                            caption: "flex justify-center py-2 mb-4 relative items-center",
                            nav_button: "h-6 w-6 bg-transparent hover:bg-primary/10 p-1 rounded transition-colors duration-300",
                            row: "flex w-full mt-2",
                            day_selected: "rounded-md bg-primary text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white",
                            day_outside: "day-outside text-foreground opacity-50 aria-selected:bg-primary-light aria-selected:text-black dark:aria-selected:text-white aria-selected:bg-opacity-10",
                            cell: "text-foreground rounded-md h-9 w-9 text-center text-sm p-0 m-0.5 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-primary/20 [&:has([aria-selected].day-outside)]:text-white [&:has([aria-selected])]:bg-primary/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                            today: `text-success`, // Add a border to today's date
                            selected: `bg-success border-success rounded-full text-white`, // Highlight the selected day
                        }}
                        components={{
                            // @ts-ignore
                            IconLeft: ({ ...props }) => (
                                <NavArrowLeft {...props} className="h-4 w-4 stroke-2" />
                            ),
                            IconRight: ({ ...props }) => (
                                <NavArrowRight {...props} className="h-4 w-4 stroke-2" />
                            ),
                        }}
                    />
                </Popover.Content>
            </Popover>
        </div>
    );
}